import { IRowNode, RowClassParams, RowClassRules } from 'ag-grid-community';
import { useCallback, useMemo, useRef } from 'react';
import { ModelWithId } from '@casops/store/apis/commonModels';
import { useSearchParamId } from '@casops/shared/hooks/useSearchParamId';
import { GridProps } from '@dmc/ui/tables';

type UseRowFocusReturn<TModel extends ModelWithId> = Pick<
  GridProps<TModel>,
  'rowClassRules' | 'ensureNodeVisibleCallback' | 'ensureNodeVisibleKey' | 'onRowClick'
>;

export const useRowFocus = <TModel extends ModelWithId>(
  paramKey: string,
): UseRowFocusReturn<TModel> => {
  const { value: modelId, toggleParam } = useSearchParamId(paramKey);

  // Highlight a specific row if there is a 'geofence' ID in the URL search params.
  const rowClassRules = useMemo<RowClassRules<TModel>>(() => {
    return {
      'dm-highlight-row': ({ data }: RowClassParams<TModel>) =>
        typeof data?.id !== 'undefined' && data.id === modelId,
    };
  }, [modelId]);

  const currentModelIdRef = useRef<number | undefined>(modelId);
  const ensureNodeVisibleKey = useRef<number>(0);
  const skipEnsureNodeVisible = useRef<boolean>(false);

  if (currentModelIdRef.current !== modelId) {
    // Keep the ref in sync with the modelId.
    currentModelIdRef.current = modelId;

    // Increment the key to trigger the effect in CasAgGrid.
    if (!skipEnsureNodeVisible.current) {
      ensureNodeVisibleKey.current++;
    } else {
      // After skipping once, re-enable the ensureNodeVisible effect for future changes.
      skipEnsureNodeVisible.current = false;
    }
  }

  // This callback signature will change every time the modelId changes.
  // If this signature change is accompanied by a new ensureNodeVisibleKey it will trigger the effect in the CasAgGrid component.
  const ensureNodeVisibleCallback = useCallback(
    (rowNode: IRowNode<TModel>): boolean => {
      return typeof modelId === 'number' && rowNode.data?.id === modelId;
    },
    [modelId],
  );

  const onRowClick = useCallback(
    ({ id }: TModel): void => {
      /**
       * Temporarily disable the ensureNodeVisible effect to prevent it from trying to scroll to the clicked row.
       * When the new currentModelId is received on the next render, the ensureNodeVisibleKey incrementation will be skipped,
       * leaving the grid scroll position unchanged.
       */
      skipEnsureNodeVisible.current = true;
      toggleParam(id);
    },
    [toggleParam],
  );

  return {
    rowClassRules,
    ensureNodeVisibleCallback,
    onRowClick,
    ensureNodeVisibleKey: ensureNodeVisibleKey.current,
  };
};

export default useRowFocus;
